import { FC, useContext } from "react";
import { Prompt, RouteComponentProps, useHistory } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import __ from "src/utils/Translations";
import {
  useFetchAbsenceGroup,
  useSaveAbsenceGroup,
  useValidateAbsenceGroupName,
} from "src/Pages/Company/Absences/api/absenceGroups.queries";
import useBasePath from "src/utils/hooks/useBasePath";
import useNativeConfirmBeforeUnload from "src/utils/jsHelpers/useNativeConfirmBeforeUnload";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import ActiveSwitch from "src/Pages/Company/Absences/Forms/ActiveSwitch";
import ColorPicker from "src/Pages/Company/Absences/Forms/ColorPicker";
import { PresenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { absenceGroupSchema } from "./AbsenceGroupsEdit.schema";

interface IMatchParams {
  groupId: string;
}

export interface IFormValues {
  active: boolean;
  name: string;
  description?: string | null;
  presenceType: PresenceType;
  color: string;
}

const AbsenceGroupsEdit: FC<RouteComponentProps<IMatchParams>> = ({
  match,
}) => {
  const { groupId } = match.params;
  const history = useHistory();
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const isNew = groupId === "new";
  const logStatus = useLogStatus();

  if (!groupId) {
    logStatus({
      level: LogLevel.WARN,
      message: "No absence group ID provided.",
      file: "AbsenceGroupsEdit.tsx",
    });
  }

  useNativeConfirmBeforeUnload();

  const {
    data: absenceGroupData,
    isFetching,
    isError,
  } = useFetchAbsenceGroup(groupId, !isNew);

  const absenceGroupMutation = useSaveAbsenceGroup({
    companyId,
    groupId,
    isNew,
  });

  const { validateGroupName } = useValidateAbsenceGroupName();

  const formMethods = useForm<IFormValues>({
    resolver: zodResolver(absenceGroupSchema),
    mode: "onChange",
    defaultValues: {
      active: true,
      name: "",
      presenceType: PresenceType.Absence,
      color: "#aabbcc",
      description: "",
    },
    values: absenceGroupData,
  });

  const {
    handleSubmit,
    setFocus,
    formState: { isDirty },
    control,
  } = formMethods;

  const onSubmit = async (formData: IFormValues) => {
    const isNameAlreadyUsed = await validateGroupName(
      companyId,
      formData.name,
      groupId
    );

    if (isNameAlreadyUsed) {
      setModal({
        open: true,
        title: __("Grupa o wskazanej nazwie już istnieje."),
        description: __("Czy mimo to chcesz kontynuować tworzenie grupy?"),
        actions: [
          {
            label: __("Utwórz grupę"),
            variant: "outlined",
            callbackFn: () => {
              absenceGroupMutation.mutate(formData);
              resetModal();
            },
          },
          {
            label: __("Zmień nazwę"),
            callbackFn: () => {
              resetModal();

              requestAnimationFrame(() => {
                setFocus("name");
              });
            },
          },
        ],
      });
    } else {
      absenceGroupMutation.mutate(formData);
    }
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Grupy nieobecności i statusy obecności"),
            to: `${basePath}/absences/absence-groups`,
          },
          {
            label: __("Edycja grupy"),
          },
        ]}
      />
      <Title
        title={
          isNew
            ? __("Dodawanie nowej grupy")
            : `${__("Edycja grupy")} ${absenceGroupData?.name || ""}`
        }
        subtitle={__(
          "Przestrzeń do tworzenia grup nieobecności i statusów obecności. Podczas edycji grupy możesz dodać nazwę, opis, ustawić kolejność wyświetlania oraz przypisać dedykowany kolor, aby wyróżnić grupę."
        )}
      />
      {isError ? (
        <ErrorDataFetching />
      ) : (
        <FormProvider {...formMethods}>
          <Paper
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{ p: "2rem" }}
            data-testid="absence-groups-edit"
          >
            <Fieldset legend={__("Widoczność grupy")}>
              <ActiveSwitch
                name="active"
                defaultValue
                data-testid="absence-group-form-field__active"
              />
            </Fieldset>
            <Fieldset legend={__("Informacje ogólne")}>
              <ControlledTextField
                name="name"
                label={__("Nazwa grupy")}
                required
                fullWidth
                maxLength={100}
                sx={{ mb: 0 }}
                data-testid="absence-group-form-field__name"
                normalizeSpaces
              />
              <ControlledTextField
                name="description"
                label={__("Opis")}
                multiline
                maxRows={3}
                maxLength={255}
                data-testid="absence-group-form-field__description"
              />
            </Fieldset>
            <Fieldset legend={__("Rodzaj grupy")}>
              <Controller
                name="presenceType"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    data-testid="absence-group-form-field__presence-type"
                  >
                    <FormControlLabel
                      value={PresenceType.Absence}
                      control={<Radio />}
                      label={__("Nieobecność")}
                      defaultChecked
                    />
                    <FormControlLabel
                      value={PresenceType.Attendance}
                      control={<Radio />}
                      label={__("Status obecności")}
                    />
                  </RadioGroup>
                )}
              />
            </Fieldset>
            <Fieldset legend={__("Nadaj kolor grupie")} divider={false}>
              <ColorPicker
                id="color"
                name="color"
                dataTestId="absence-group-form-field__color"
              />
            </Fieldset>
            <Box
              sx={{
                textAlign: "right",
                margin: "2rem 0 0 1rem",
              }}
            >
              <Button
                variant="outlined"
                type="button"
                size="large"
                sx={{ marginRight: "1rem" }}
                onClick={() => history.goBack()}
              >
                {__("Anuluj")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                loading={absenceGroupMutation.isPending}
                data-testid="absence-group-form__submit-button"
              >
                {__("Zapisz grupę")}
              </Button>
            </Box>
            {isFetching && <Loader />}
          </Paper>
          <Prompt
            when={isDirty && !absenceGroupMutation.isSuccess}
            message={__("Niezapisane zmiany zostaną utracone.")}
          />
        </FormProvider>
      )}
    </>
  );
};

export default AbsenceGroupsEdit;
